import { Card, Fields, Menu, Row } from "@/components/collection"
import { useDateFnsLocaleFormat, useDictionary } from "@/dictionaries/hooks"
import { useSelectItem } from "@/hooks/useSelect"
import { useTranslation } from "@/store/languages/hooks"
import { useMediasFile } from "@/store/medias/hooks"
import { Page } from "@/store/pages/localizers"
import { CalendarClock, CalendarPlus, Eye, EyeOff, ImageIcon, Lock } from "lucide-react"
import { Context, ContextMenu, usePageContext } from "./Context"

/**
 * dictionary src/dictionaries/en/pages/dashboard/pages.json
 */
const dictionary = createContextMapper("pages", "dashboard", "pages")

/**
 * Item
 */
export const Item: React.FC<{ page: Page }> = ({ page }) => {
  const { view } = usePageContext()
  const { selectItemProps } = useSelectItem(Context, page.id, () =>
    navigate(`/dashboard/pages/${page.id}`)
  )
  return (
    <Menu type="context-menu" menu={<ContextMenu page={page} />} asChild>
      <div className="grid" {...selectItemProps}>
        {view === "grid" ? <ItemCard {...{ page }} /> : <ItemRow {...{ page }} />}
      </div>
    </Menu>
  )
}

/**
 * ItemCard
 */
const ItemCard: React.FC<{ page: Page }> = ({ page }) => {
  const { _ } = useDictionary(dictionary())
  const format = useDateFnsLocaleFormat()
  const { isSelected } = useSelectItem(Context, page.id)
  const t = useTranslation()
  const { title, description } = t(page.seo)
  const image = useMediasFile(t(page.seo).image)

  return (
    <Card className="relative overflow-hidden" selected={isSelected}>
      {page.lock && (
        <span className="absolute top-2 left-2 flex justify-center items-center size-8 rounded-full bg-background/50 text-foreground">
          <Lock aria-hidden size={16} />
        </span>
      )}
      <Card.Image src={image?.previewUrl} className="aspect-video">
        <ImageIcon size={64} strokeWidth={1} />
      </Card.Image>
      <Card.Header className="grow">
        <Card.Title>{textPlaceholder(title, _("no-title"))}</Card.Title>
        <Card.Description>{textPlaceholder(description, _("no-description"))}</Card.Description>
        <Card.Menu menu={<ContextMenu page={page} />} />
      </Card.Header>
      <Card.Content>
        <Fields divider>
          <Fields.Item
            name={_("state")}
            icon={<Eye aria-hidden />}
            value={_(`state-${page.state}`)}
            stretch
          />
          <Fields.Item
            name={_("created-at")}
            icon={<CalendarPlus aria-hidden />}
            value={format(page.createdAt, "PPpp")}
            stretch
          />
          <Fields.Item
            name={_("updated-at")}
            icon={<CalendarClock aria-hidden />}
            value={format(page.updatedAt, "PPpp")}
            stretch
          />
        </Fields>
      </Card.Content>
    </Card>
  )
}

/**
 * ItemRow
 */
const ItemRow: React.FC<{ page: Page }> = ({ page }) => {
  const { _ } = useDictionary(dictionary())

  const { isSelected } = useSelectItem(Context, page.id)
  const format = useDateFnsLocaleFormat()
  const t = useTranslation()
  const { title } = t(page.seo)
  const image = useMediasFile(t(page.seo).image)
  return (
    <Row selected={isSelected} className={cx(status === "deleted" && "opacity-75")}>
      <Row.Image src={image?.previewUrl} alt={image ? t(image).alt : undefined}>
        <ImageIcon size={12} className="text-foreground/50" aria-hidden />
      </Row.Image>
      <Row.Header>
        <Row.Title>{textPlaceholder(title, _("no-title"))}</Row.Title>
        <Row.Description>
          {_("created-at")} {format(page.createdAt, "PP")}
        </Row.Description>
      </Row.Header>
      <Row.Content>
        <Row.Icon tooltip={_(`state-${page.state}`)}>
          {page.state === "draft" && <EyeOff aria-hidden />}
          {page.state === "published" && <Eye aria-hidden />}
        </Row.Icon>
      </Row.Content>
      <Row.Menu menu={<ContextMenu page={page} />} />
    </Row>
  )
}

/**
 * helpers
 */
const textPlaceholder = (value: string, placeholder: string) =>
  S.isEmpty(S.trim(value ?? "")) ? placeholder : value
